import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentStatus({ cancelled = false }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancel" : "checking");

  const poll = useCallback(
    async (attempt) => {
      if (attempt >= MAX_POLLS) {
        setState("fail");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("fail");
          return;
        }
      } catch (e) {
        // keep polling
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId]
  );

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) {
      setState("fail");
      return;
    }
    poll(0);
  }, [cancelled, sessionId, poll]);

  const config = {
    checking: { icon: <Loader2 size={56} className="text-cyan animate-spin" />, title: t("pay.checking"), msg: "" },
    success: { icon: <CheckCircle2 size={56} className="text-cyan" />, title: t("pay.successTitle"), msg: t("pay.successMsg") },
    fail: { icon: <XCircle size={56} className="text-red-400" />, title: t("pay.failTitle"), msg: t("pay.failMsg") },
    cancel: { icon: <XCircle size={56} className="text-neutral-400" />, title: t("pay.cancelTitle"), msg: t("pay.cancelMsg") },
  }[state];

  return (
    <div data-testid="payment-status-page" data-state={state} className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="radial-cyan absolute inset-0 pointer-events-none" />
      <div className="relative glass rounded-3xl border border-cyan-500/15 p-10 md:p-14 max-w-lg w-full text-center flex flex-col items-center gap-5">
        {config.icon}
        <h1 className="font-display font-700 uppercase tracking-tight text-2xl md:text-3xl">{config.title}</h1>
        {config.msg && <p className="text-neutral-400 font-light">{config.msg}</p>}
        <Link
          to="/"
          data-testid="payment-back-home"
          className="mt-4 inline-flex items-center gap-2 text-black font-medium text-sm px-6 py-3 rounded-full hover:brightness-110 transition-all duration-300"
          style={{ backgroundColor: "#C9A96A" }}
        >
          <Home size={16} /> {t("pay.back")}
        </Link>
      </div>
    </div>
  );
}
